<script setup>
  import { inject, nextTick, onUnmounted, ref, computed } from "vue";
  import { storeToRefs } from "pinia";
  import Draggable from "vuedraggable";
  import { Copy, Ellipsis, SeparatorHorizontal, GripVertical, EyeOff, Funnel, Pencil, Plus, Trash2 } from "lucide-vue-next";

  import { Card } from "@/components/ui/card";
  import { Button } from "@/components/ui/button";
  import { Checkbox } from "@/components/ui/checkbox";
  import { Tooltip } from "@/components/ui/tooltip";
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
  } from "@/components/ui/dropdown-menu";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";

  import CardTypePickerDialog from "@/components/Survey/Edit/CardTypePickerDialog.vue";
  import ErrorsTooltip from "@/components/Survey/Components/ErrorsTooltip.vue";
  import SectionHeader from "./SectionHeader.vue";

  import { useQuestionCardTypes, useCardAttributes } from "@utils/composables/questionCard";
  import { getConditionsOverviewText } from "@utils/composables/conditionsOverview";
  import { isEmpty, removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
  import { useSurveySectionsStore } from "@stores/survey/surveySections";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useReviewCommentsStore } from "@stores/survey/reviewComments";
  import { getCardGroupColors } from "@utils/constants/cardTypes";

  const props = defineProps({
    cardSubTypesInfos: { type: Object, required: true },
    cards: { type: Array, required: true },
    errorsByCard: { type: Object, default: () => ({}) },
    eventBusName: { type: String, required: true },
    isCardFilteredOut: { type: Function, required: true },
    isFilterActive: { type: Boolean, required: true },
    isListReadOnly: { type: Boolean, required: true },
    selectedCard: { type: Object, default: () => ({}) },
    selectedCardToken: { type: [String, Array], default: "" },
    onlyQuestion: { type: Boolean, default: false },
    isFooterHidden: { type: Boolean, default: false },
    isTestMode: { type: Boolean, default: false },
    questionCount: { type: Number, default: 0 },
  });

  const sectionsStore = useSurveySectionsStore();
  const { sections, selectedCardTokens, cardsBySection } = storeToRefs(sectionsStore);
  const {
    isCardMultiSelect,
    isCollapsedSection,
    dropCardsIntoSection,
    dragCardFromSection,
    dropSection,
    toggleCardSelection,
    resetSelectedCards,
    createSection,
  } = sectionsStore;

  // Direct store access for search filtering to ensure reactivity
  const surveyCardsStore = useSurveyCardsStore();
  const reviewStore = useReviewCommentsStore();

  // Computed set of filtered card tokens for reactive re-renders
  const filteredCardTokens = computed(() => {
    if (!surveyCardsStore.searchQuery?.trim()) {
      return new Set();
    }
    return new Set(props.cards.filter((card) => surveyCardsStore.isCardFilteredOut(card)).map((card) => card.token));
  });

  // Computed flag to know if search is active - reactive
  const isSearchActive = computed(() => !isEmpty(surveyCardsStore.searchQuery));

  const innerRef = ref();
  const scrollAreaRef = ref(null);
  const dragCards = ref(false);
  const draggedCardToken = ref(null);
  const hoveredIconToken = ref(null);

  // Check if any cards are selected across all sections
  const hasAnySelectedCard = computed(() => selectedCardTokens.value.length > 0);

  const { determineIcon } = useQuestionCardTypes(props.cardSubTypesInfos);
  const { name, token, isHidden, hasConditions, type, cardsTitle } = useCardAttributes();

  const eventBus = inject(props.eventBusName);
  const handleCommand = (command, token) => eventBus.emit({ command, token });

  const disableCards = props.onlyQuestion ? [] : ["_gen_targets_", "_gen_profiles_"];

  const isCardSelectedAsCurrent = (cardToken) => {
    if (Array.isArray(props.selectedCardToken)) return props.selectedCardToken.includes(cardToken);
    return props.selectedCardToken === cardToken;
  };

  const getCards = (cards) => {
    if (props.onlyQuestion) return cards.filter((c) => c.groupType === "question");
    return cards;
  };

  const hasErrors = (cardToken) => !isEmpty(props.errorsByCard?.[cardToken]);
  const cardErrors = (cardToken) => props.errorsByCard?.[cardToken] ?? [];

  const sectionCards = (sectionToken) => {
    const sectionIndex = sections.value.findIndex((section) => section.token === sectionToken);
    return cardsBySection.value[sectionIndex] ?? [];
  };

  const renderedCards = (cards) => cards.filter((card) => !disableCards.includes(card.token));

  let cardsBeforeDrag = {};

  const handleStartDrag = ({ oldIndex, from }) => {
    const sectionToken = from.__draggable_component__?.itemKey ?? "";
    cardsBeforeDrag = Object.fromEntries(
      sections.value.map((section, index) => [section.token, [...(cardsBySection.value[index] ?? [])]]),
    );

    const cardToken = renderedCards(cardsBeforeDrag[sectionToken] ?? [])[oldIndex]?.token;
    if (cardToken && dragCardFromSection(cardToken)) {
      dragCards.value = true;
      draggedCardToken.value = cardToken;
    }
  };

  const handleAfterDrop = ({ newIndex, to }) => {
    const newSectionToken = to.__draggable_component__?.itemKey ?? "";
    const previousCards = cardsBeforeDrag[newSectionToken] ?? sectionCards(newSectionToken);

    const dropAfterCard = renderedCards(previousCards)
      .filter((card) => card.token !== draggedCardToken.value)
      .slice(0, newIndex)
      .findLast((card) => !isCardMultiSelect(card.token));

    const remainingCards = previousCards.filter((card) => !isCardMultiSelect(card.token));
    const indexInSection = dropAfterCard
      ? remainingCards.findIndex((card) => card.token === dropAfterCard.token) + 1
      : 0;

    const movedTokens = dropCardsIntoSection(newSectionToken, indexInSection);
    if (movedTokens.length) eventBus.emit({ command: "applyDrag" });
    dragCards.value = false;
    draggedCardToken.value = null;
    cardsBeforeDrag = {};
  };

  const handleStartDragSection = ({ oldIndex }) => {
    dragCards.value = true;
    selectedCardTokens.value = cardsBySection.value[oldIndex].map(({ token }) => token);
  };

  const handleAfterDropSection = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      dropSection(newIndex);
      eventBus.emit({ command: "applyDrag" });
    }
    dragCards.value = false;
    resetSelectedCards();
    draggedCardToken.value = null;
  };

  const handleAction = async (actionName, cardToken) => {
    const isFilterOn = Boolean(props.isFilterActive);

    if (!disableCards.includes(cardToken) && actionName !== "scroll") handleCommand(actionName, cardToken);

    if (isFilterOn) cardScrollTopScrollbar();
  };

  const { isCardBindedToTarget } = useSurveyTargetsStore();

  const {
    isOpen: isDeleteCardOpen,
    requestDelete: requestCardDelete,
    confirm: confirmCardDelete,
  } = useConfirmDelete((card) => {
    handleCommand("remove", card.token);
    handleAction("scroll", card.token);
  });

  const lastPickedToken = ref(null);

  const handleMultiSelectAction = (cardToken) => {
    if (props.isListReadOnly) return;
    toggleCardSelection(cardToken);
    lastPickedToken.value = cardToken;
  };

  const handleCheckboxClick = (cardToken, event) => {
    if (event.shiftKey) return handleRangeSelectAction(cardToken);
    handleMultiSelectAction(cardToken);
  };

  const handleRangeSelectAction = (cardToken) => {
    if (props.isListReadOnly) return;
    const order = cardsBySection.value.flat().map(({ token }) => token);
    const openToken = Array.isArray(props.selectedCardToken) ? props.selectedCardToken.at(-1) : props.selectedCardToken;
    const anchor = lastPickedToken.value ?? openToken;
    const from = order.indexOf(anchor);
    const to = order.indexOf(cardToken);
    if (from === -1 || to === -1) return handleMultiSelectAction(cardToken);

    const [start, end] = from < to ? [from, to] : [to, from];
    const range = order.slice(start, end + 1);
    selectedCardTokens.value = [...new Set([...selectedCardTokens.value, ...range])];
    lastPickedToken.value = cardToken;
  };

  const cardScrollTopScrollbar = async () => {
    await nextTick();

    const targetCard = document.getElementById(`nav-menu-item-${props.selectedCardToken}`);
    if (targetCard && scrollAreaRef.value) {
      scrollAreaRef.value.scrollTop = targetCard.offsetTop - 80;
    }
  };

  const scrollToCard = async () => {
    await nextTick();

    const targetCard = document.getElementById(`nav-menu-item-${props.selectedCardToken}`);
    const footer = document.getElementById(`nav-pane-footer`)?.getBoundingClientRect();
    const layoutTargetCard = targetCard?.getBoundingClientRect();

    if (targetCard && footer && layoutTargetCard && scrollAreaRef.value) {
      const distance = Math.round(layoutTargetCard.top - footer.top + 1);
      scrollAreaRef.value.scrollTop = distance + scrollAreaRef.value.scrollTop + 65;
    }
  };

  const addCardDialogOpen = ref(false);
  const addCardToken = ref(null);

  const openAddCardDialog = (cardToken) => {
    addCardToken.value = cardToken;
    addCardDialogOpen.value = true;
  };

  const handleAddCard = (subType) => {
    const cardToken = addCardToken.value;
    const cardIndex = props.cards.findIndex((c) => c.token === cardToken);
    const currentSectionIndex = sections.value.findIndex((s) => s.index > cardIndex) - 1;

    const isLastCard = cardIndex === props.cards.length - 1;
    const isLastSection = currentSectionIndex === sections.value.length - 1;

    if (isLastCard && isLastSection) {
      eventBus.emit({ command: "addAtTheEnd", type: subType });
      cardScrollTopScrollbar();
    } else {
      eventBus.emit({ command: "add", token: cardToken, type: subType });
    }
    handleAction("scroll", cardToken);

    const cardClicked = document.getElementById(`nav-menu-item-${cardToken}`);
    const layoutCardClicked = cardClicked?.getBoundingClientRect();
    const footer = document.getElementById(`nav-pane-footer`)?.getBoundingClientRect();

    const layoutCard = 65;
    const isLastCardCurrently = (layoutCardClicked?.top ?? 0) + layoutCard;

    if (footer && footer.top < isLastCardCurrently + 20) {
      scrollToCard();
    }
  };

  const isCardHidden = ({ card, section }) => {
    // Cards created in charts are always hidden
    if (card.is_for_analytics) return true;

    // Filtered out by search
    if (filteredCardTokens.value.has(card.token)) return true;

    // Collapsed section (only when search is not active)
    if (!isSearchActive.value && isCollapsedSection(section.token)) return true;

    // Hide during drag if card is multi-selected (except the dragged card itself)
    if (dragCards.value && isCardMultiSelect(card.token)) return card.token !== draggedCardToken.value;

    return false;
  };

  defineExpose({
    scrollAreaRef,
    cardScrollTopScrollbar,
    scrollToCard,
  });

  onUnmounted(() => {
    resetSelectedCards();
  });
</script>

<template>
  <div class="flex-1 flex flex-col gap-6 min-h-0">
    <Card
      class="border-0 shadow-none flex-1 flex flex-col min-h-0 overflow-hidden !py-0"
      :class="{ 'bg-transparent': true }">
      <div
        ref="scrollAreaRef"
        class="flex-1 overflow-y-auto px-2">
        <Draggable
          :disabled="isListReadOnly"
          :list="sections"
          item-key="sections"
          group="sections"
          draggable=".draggable-element"
          handle=".drag-handle"
          @start="handleStartDragSection"
          @end="handleAfterDropSection">
          <template #item="{ element: section, index: sectionIndex }">
            <div
              class="section mb-0.5"
              :class="{
                'draggable-element': sectionIndex != 0,
              }"
              data-cy="cards-list-section">
              <SectionHeader
                :section="section"
                :section-index="sectionIndex"
                :is-list-read-only="isListReadOnly" />
              <Draggable
                ref="innerRef"
                :disabled="isListReadOnly"
                :list="getCards(cardsBySection[sectionIndex])"
                :item-key="`${section.token}`"
                group="cards"
                handle=".card-drag-handle"
                class="space-y-0.5"
                :drag-options="{ animation: 500 }"
                @start="handleStartDrag"
                @end="handleAfterDrop">
                <template #item="{ element: card }">
                  <div
                    v-if="!disableCards.includes(token(card))"
                    v-show="!isCardHidden({ card, section })"
                    data-cy="cards-list-item">
                    <div
                      :id="`nav-menu-item-${token(card)}`"
                      :key="`nav-menu-item-${token(card)}`"
                      :class="{
                        'bg-secondary': isCardMultiSelect(token(card)),
                        'bg-muted shadow-[inset_0_0_0_1px_var(--foreground)]': isTestMode && isCardSelectedAsCurrent(token(card)),
                        'shadow-[inset_0_0_0_1px_var(--ring)]': !isTestMode && isCardSelectedAsCurrent(token(card)),
                        'shadow-[inset_0_0_0_1px_var(--destructive)]': hasErrors(token(card)),
                      }"
                      :title="removeHtmlMarkdownAndLineBreak(cardsTitle(card))"
                      class="group flex cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors hover:bg-muted"
                      @mousedown.shift.prevent
                      @click.exact="handleAction('select', token(card))"
                      @click.ctrl.stop="handleMultiSelectAction(token(card))"
                      @click.meta.stop="handleMultiSelectAction(token(card))"
                      @click.shift.exact.stop="handleRangeSelectAction(token(card))">
                      <!-- Left zone: drag handle + icon/checkbox -->
                      <div
                        class="flex items-center"
                        @click.stop
                        @mouseenter="hoveredIconToken = token(card)"
                        @mouseleave="hoveredIconToken = null">
                        <div
                          v-if="!isListReadOnly"
                          :class="hoveredIconToken === token(card) ? 'opacity-100' : 'opacity-0'"
                          class="card-drag-handle w-4 shrink-0 cursor-grab text-muted-foreground transition-opacity hover:text-foreground active:cursor-grabbing">
                          <GripVertical class="h-4 w-4" />
                        </div>
                        <div class="relative size-6 shrink-0">
                          <!-- Checkbox (visible on hover, when selected, or when any card is selected) -->
                          <div
                            v-if="
                              isCardMultiSelect(token(card)) ||
                              hasAnySelectedCard ||
                              hoveredIconToken === token(card)
                            "
                            class="flex size-6 cursor-pointer items-center justify-center"
                            @click.stop="handleCheckboxClick(token(card), $event)">
                            <Checkbox
                              :model-value="isCardMultiSelect(token(card))"
                              class="size-4" />
                          </div>
                          <!-- Icon (visible when no hover and no selection) -->
                          <div
                            v-else
                            class="flex size-6 cursor-pointer items-center justify-center rounded-md transition-colors"
                            :class="[getCardGroupColors(card).bg, getCardGroupColors(card).text]"
                            @click="handleMultiSelectAction(token(card))">
                            <component
                              :is="determineIcon(type(card))"
                              class="size-3.5"
                              :stroke-width="1.8" />
                          </div>
                          <span
                            v-if="reviewStore.hasOpenComments(token(card))"
                            class="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-status-info ring-2 ring-background pointer-events-none"
                            title="Unresolved comments" />
                        </div>
                      </div>
                      <div class="flex min-w-0 flex-1 items-baseline gap-2">
                        <span class="min-w-0 truncate text-13 font-medium text-primary">
                          {{ name(card) || "Untitled" }}
                        </span>
                        <ErrorsTooltip
                          v-if="hasErrors(token(card)) && !isListReadOnly"
                          :errors="cardErrors(token(card))"
                          :card-name="name(card)" />
                        <span class="min-w-0 flex-1 truncate text-xs text-foreground-subtle">
                          {{ removeHtmlMarkdownAndLineBreak(cardsTitle(card)) }}
                        </span>
                      </div>
                      <div
                        class="flex items-center gap-0.5 shrink-0"
                        @click.stop>
                        <Tooltip
                          v-if="isTestMode"
                          content="Edit">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            class="size-6 opacity-0 transition-opacity group-hover:opacity-100"
                            @click="handleAction('goToEdit', token(card))">
                            <Pencil class="size-3.5" />
                          </Button>
                        </Tooltip>
                        <Tooltip
                          v-if="isHidden(card)"
                          content="Hidden">
                          <Button
                            v-if="!isListReadOnly"
                            variant="ghost"
                            size="icon-sm"
                            class="size-6 text-muted-foreground"
                            @click="handleAction('selectConditions', token(card))">
                            <EyeOff class="size-3.5" />
                          </Button>
                          <span
                            v-else
                            class="p-1 text-muted-foreground">
                            <EyeOff class="size-3.5" />
                          </span>
                        </Tooltip>
                        <Tooltip
                          v-else-if="hasConditions(card)"
                          :content="getConditionsOverviewText(card?.specs?.conditions)">
                          <Button
                            v-if="!isListReadOnly"
                            variant="ghost"
                            size="icon-sm"
                            class="size-6 text-muted-foreground"
                            @click="handleAction('selectConditions', token(card))">
                            <Funnel class="size-3.5" />
                          </Button>
                          <span
                            v-else
                            class="p-1 text-muted-foreground">
                            <Funnel class="size-3.5" />
                          </span>
                        </Tooltip>
                      </div>
                      <div
                        v-if="!isListReadOnly"
                        class="flex items-center gap-0.5 shrink-0 w-0 overflow-hidden group-hover:w-auto group-hover:overflow-visible transition-all duration-200"
                        @click.stop>
                        <Tooltip content="Add card">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            class="size-6 text-muted-foreground"
                            @click="openAddCardDialog(token(card))">
                            <Plus class="size-3.5" />
                          </Button>
                        </Tooltip>
                        <DropdownMenu>
                          <DropdownMenuTrigger as-child>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              class="size-6 text-muted-foreground"
                              aria-label="Card actions">
                              <Ellipsis class="size-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            class="font-sans text-sm">
                            <DropdownMenuItem
                              :disabled="disableCards.includes(token(card))"
                              @click="handleAction('duplicate', token(card))">
                              <Copy class="mr-2 size-3.5" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              :disabled="disableCards.includes(token(card))"
                              @click="createSection({ token: token(card) })">
                              <SeparatorHorizontal class="mr-2 size-3.5" />
                              Add section below
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              class="text-status-error-strong"
                              :disabled="disableCards.includes(token(card)) || isCardBindedToTarget(token(card))"
                              @click="requestCardDelete(card)">
                              <Trash2 class="mr-2 size-3.5" />
                              <div class="flex flex-col">
                                <span>Delete</span>
                                <span
                                  v-if="isCardBindedToTarget(token(card))"
                                  class="text-xs text-muted-foreground">
                                  Bound to a target
                                </span>
                              </div>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                </template>
              </Draggable>
            </div>
          </template>
        </Draggable>
      </div>
    </Card>
    <ConfirmDialog
    v-model:open="isDeleteCardOpen"
    title="Delete card"
    description="Are you sure you want to delete this card? This change cannot be undone."
    confirm-label="Yes, delete"
    cancel-label="No, cancel"
    @confirm="confirmCardDelete" />

  <CardTypePickerDialog
      v-model:open="addCardDialogOpen"
      title="Add card"
      description="Select a card type to add"
      @select="handleAddCard" />
  </div>
</template>
